import { useEffect, type ReactNode } from 'react'
import useAuthStore from '@/store/useAuthStore'
import useBillingStore from '@/store/useBillingStore'
import useGetBillingSubscription from '@/services/api/billing/useGetBillingSubscription'
import BillingBlockedScreen from './BillingBlockedScreen'

/**
 * Renders the billing recovery screen whenever the httpClient interceptor has
 * flagged a subscription-level 421 (codes 0-3). Auto-clears once the current
 * space's billing looks healthy again (e.g. after switching spaces).
 */
export default function BillingBlockGate({
  children,
}: {
  children: ReactNode
}) {
  const storedBlock = useBillingStore((s) => s.block)
  const clearBlock = useBillingStore((s) => s.clearBlock)
  const currentTeamId = useAuthStore((s) => s.currentTeamId)

  // A block belongs to the space whose request failed. After switching space
  // it is simply ignored — clearing it by hand raced the new space's own 421s.
  const block =
    storedBlock &&
    (storedBlock.teamId == null ||
      String(storedBlock.teamId) === String(currentTeamId))
      ? storedBlock
      : null

  const { data, isSuccess } = useGetBillingSubscription({
    teamId: currentTeamId,
    enabled: !!block,
    refetchOnMount: 'always',
  })

  useEffect(() => {
    if (block && isSuccess && data?.has_active_subscription) {
      clearBlock()
    }
  }, [block, isSuccess, data?.has_active_subscription, clearBlock])

  if (block) return <BillingBlockedScreen block={block} />

  return <>{children}</>
}
